import { AdminOrderRow, PartnerUser, Warehouse } from '../../../models/APIModels';
import { AdminPartner } from '../../../components/admin/CreatePartnerModal';
import { byName, isCourierPartner } from '../../../services/adminPartners';

/**
 * What the Assign modal edits. A `<select>` hands back strings, so an id field
 * holds a number when it was loaded and a string once someone has changed it;
 * '' is "not assigned". Read ids through {@link idOf}, never with `Number()`
 * alone — `Number('')` is 0, not "nothing".
 */
export interface AssignmentFormValues {
  collectionCourierId: number | string;
  deliveryCourierId: number | string;
  partnerId: number | string;
  /**
   * The return leg's own partner; '' is "the collection partner returns too".
   * Optional so a form that predates per-leg partners still type-checks.
   */
  returnPartnerId?: number | string;
  warehouseId: number | string;
  collectionEta: string;
  deliveryEta: string;
  collectionEtaStart: string;
  collectionEtaEnd: string;
  deliveryEtaStart: string;
  deliveryEtaEnd: string;
}

const ID_FIELDS = ['partnerId', 'returnPartnerId', 'collectionCourierId', 'deliveryCourierId', 'warehouseId'] as const;
const TEXT_FIELDS = [
  'collectionEta',
  'deliveryEta',
  'collectionEtaStart',
  'collectionEtaEnd',
  'deliveryEtaStart',
  'deliveryEtaEnd',
] as const;

export function idOf(value: number | string | null | undefined): number | null {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The body for `PUT /logistics/admin/orders/{id}/assignment`: only what changed.
 * An id that was cleared goes as `null` and an ETA as `''`, which the API reads
 * as "clear it"; anything not in the body is left alone.
 *
 * The modal used to send every non-empty field on every save and nothing for an
 * emptied one, so a courier could not be taken off an order at all, and editing
 * an ETA re-sent the couriers with it.
 */
export function buildAssignmentPayload(
  initial: AssignmentFormValues,
  values: AssignmentFormValues
): Record<string, number | string | null> {
  const payload: Record<string, number | string | null> = {};
  ID_FIELDS.forEach((key) => {
    const before = idOf(initial[key]);
    const after = idOf(values[key]);
    if (before !== after) payload[key] = after;
  });
  TEXT_FIELDS.forEach((key) => {
    const before = initial[key] || '';
    const after = values[key] || '';
    if (before !== after) payload[key] = after;
  });
  return payload;
}

export interface PartnerChoice {
  id: number;
  name: string;
  /** On the order already but not offered for new assignments (another type, deactivated, deleted). */
  current?: boolean;
}

/**
 * The order's partner: a partner that runs collections and deliveries. The
 * current one is always offered, even when it would not be now, so the select
 * shows what the order actually has rather than "Not assigned".
 */
export function courierPartnerChoices(
  partners: AdminPartner[],
  currentId: number | null,
  currentName?: string | null
): PartnerChoice[] {
  const choices: PartnerChoice[] = byName(partners.filter(isCourierPartner)).map((p) => ({
    id: p.id,
    name: p.name,
  }));
  if (currentId != null && !choices.some((c) => c.id === currentId)) {
    const known = partners.find((p) => p.id === currentId);
    choices.push({
      id: currentId,
      name: known?.name || currentName || `Partner #${currentId}`,
      current: true,
    });
  }
  return choices;
}

/** The drivers who can take an order for this partner. None until a partner is chosen. */
export function couriersFor(couriers: PartnerUser[], partnerId: number | null): PartnerUser[] {
  if (partnerId == null) return [];
  return couriers.filter((c) => c.partnerId === partnerId);
}

/**
 * When a leg moves to another partner, a courier who does not work for it
 * cannot stay. The API refuses the save if one would; this clears them in the
 * form first, so the admin sees it happen instead of reading a refusal. The
 * return leg has its own partner since v2.18, and follows the collection
 * partner when it has none, which is what leaving out the last argument means.
 */
export function couriersAfterPartnerChange(
  values: Pick<AssignmentFormValues, 'collectionCourierId' | 'deliveryCourierId'>,
  couriers: PartnerUser[],
  nextPartnerId: number | null,
  nextReturnLegPartnerId: number | null = nextPartnerId
): Pick<AssignmentFormValues, 'collectionCourierId' | 'deliveryCourierId'> {
  const keepFor = (partnerId: number | null) => {
    const allowed = new Set(couriersFor(couriers, partnerId).map((c) => c.id));
    return (v: number | string) => {
      const id = idOf(v);
      return id != null && allowed.has(id) ? v : '';
    };
  };
  return {
    collectionCourierId: keepFor(nextPartnerId)(values.collectionCourierId),
    deliveryCourierId: keepFor(nextReturnLegPartnerId)(values.deliveryCourierId),
  };
}

type LegOrder = Pick<AdminOrderRow, 'collectionPartnerId' | 'returnPartnerId' | 'hubspotReturnCourier'>;

/**
 * The return partner the form starts with. The order row says who holds the
 * return leg, the collection partner included when it returns too; that case is
 * '' here, so the select reads "Same as collection".
 */
export function initialReturnPartnerId(order: LegOrder): number | '' {
  const holder = order.returnPartnerId ?? null;
  if (holder == null) return '';
  const followsCollection = !order.hubspotReturnCourier && holder === order.collectionPartnerId;
  return followsCollection ? '' : holder;
}

/**
 * Who will hold the return leg once this form is saved, by the API's rule
 * (storage-api `OrderLegs`): the return partner if one is chosen, otherwise
 * the collection partner -- unless HubSpot names a return courier no partner
 * here is linked to ("Disposal (no return)") and the admin has not cleared it,
 * in which case nobody here is doing the return.
 */
export function returnLegPartnerId(
  values: Pick<AssignmentFormValues, 'partnerId' | 'returnPartnerId'>,
  initial: Pick<AssignmentFormValues, 'returnPartnerId'>,
  order: Pick<AdminOrderRow, 'hubspotReturnCourier'>
): number | null {
  const explicit = idOf(values.returnPartnerId);
  if (explicit != null) return explicit;
  const cleared = idOf(initial.returnPartnerId) != null;
  if (order.hubspotReturnCourier && !cleared) return null;
  return idOf(values.partnerId);
}

export interface WarehouseGroup {
  partnerId: number;
  partnerName: string;
  warehouses: Warehouse[];
}

/**
 * Warehouse locations grouped under the partner that runs them. A location's
 * own name ("Main Warehouse") rarely says whose it is, and with more than one
 * warehouse partner that is the question being answered.
 */
export function warehouseGroups(warehouses: Warehouse[], partners: AdminPartner[]): WarehouseGroup[] {
  const groups = new Map<number, WarehouseGroup>();
  warehouses.forEach((wh) => {
    const pid = wh.partnerId ?? 0;
    if (!groups.has(pid)) {
      const owner = partners.find((p) => p.id === pid);
      groups.set(pid, {
        partnerId: pid,
        partnerName: owner?.name || (pid ? `Partner #${pid}` : 'Unknown partner'),
        warehouses: [],
      });
    }
    groups.get(pid)!.warehouses.push(wh);
  });
  return Array.from(groups.values())
    .map((g) => ({ ...g, warehouses: byName(g.warehouses) }))
    .sort((a, b) => a.partnerName.localeCompare(b.partnerName));
}
